// Käyttäjä
export interface User {
  id: string;
  email: string;
  name: string;
  pictureUrl?: string;
}

// Autentikointi
export interface AuthStatus {
  authenticated: boolean;
  user: User | null;
}

// Liike
export interface Exercise {
  id: string;
  userId: string | null;
  name: string;
  muscleGroup: MuscleGroup | null;
  exerciseType: ExerciseType | null;
  isCustom: boolean;
  createdAt: string;
}

export type MuscleGroup = 'chest' | 'back' | 'shoulders' | 'legs' | 'arms' | 'core' | 'cardio';
export type ExerciseType = 'compound' | 'isolation' | 'cardio';

export const MUSCLE_GROUPS: { id: MuscleGroup; name: string; emoji: string }[] = [
  { id: 'chest', name: 'Rinta', emoji: '💪' },
  { id: 'back', name: 'Selkä', emoji: '🔙' },
  { id: 'shoulders', name: 'Olkapäät', emoji: '🎯' },
  { id: 'legs', name: 'Jalat', emoji: '🦵' },
  { id: 'arms', name: 'Käsivarret', emoji: '💪' },
  { id: 'core', name: 'Keskivartalo', emoji: '🎯' },
  { id: 'cardio', name: 'Cardio', emoji: '❤️' },
];

export type WorkoutType = 'push' | 'pull' | 'legs' | 'upper' | 'lower' | 'full_body' | 'cardio' | 'other';

export const WORKOUT_TYPES: { id: WorkoutType; name: string }[] = [
  { id: 'push', name: 'Push' },
  { id: 'pull', name: 'Pull' },
  { id: 'legs', name: 'Jalat' },
  { id: 'upper', name: 'Yläkroppa' },
  { id: 'lower', name: 'Alakroppa' },
  { id: 'full_body', name: 'Kokovartalo' },
  { id: 'cardio', name: 'Cardio' },
  { id: 'other', name: 'Muu' },
];

// Treenin sarja (yksittäinen sarja)
export interface WorkoutSetData {
  id?: string;
  setNumber: number;
  reps: number;
  weight: number | null;
  rpe: number | null;
  restSeconds: number | null;
  notes: string | null;
}

// Treenin liike (liike + sen sarjat)
export interface WorkoutExercise {
  id: string;
  exerciseId: string;
  exercise: Exercise | null;
  orderIndex: number;
  notes: string | null;
  sets: WorkoutSetData[];
}

// Linkitetty aktiviteetti (Garmin)
export interface LinkedActivity {
  id: string;
  externalId?: string;
  activityType: string;
  activityName: string | null;
  startTime?: string;
  endTime?: string | null;
  durationMinutes: number | null;
  calories: number | null;
  avgHeartRate: number | null;
  maxHeartRate: number | null;
  minHeartRate: number | null;
  distance: number | null;
  steps?: number | null;
  aerobicTE: number | null;
  anaerobicTE: number | null;
  moderateMinutes?: number | null;
  vigorousMinutes?: number | null;
  avgSpeed?: number | null;
  maxSpeed?: number | null;
  elevationGain?: number | null;
  elevationLoss?: number | null;
  source: string;
}

// Käytettävät treenityypit välilehdissä
export type FilterWorkoutType = 'upper' | 'legs' | 'full_body' | 'cardio' | 'other';

export const FILTER_WORKOUT_TYPES: { id: FilterWorkoutType; name: string }[] = [
  { id: 'upper', name: 'Yläkroppa' },
  { id: 'legs', name: 'Jalat' },
  { id: 'full_body', name: 'Kokovartalo' },
  { id: 'cardio', name: 'Cardio' },
  { id: 'other', name: 'Muu' },
];

// Treeni - vastaa API:n todellista rakennetta
export interface Workout {
  id: string;
  userId: string;
  name: string;
  notes: string | null;
  workoutType: FilterWorkoutType | null;
  source: string | null; // 'garmin' = automaattisesti Garminista tuotu
  startedAt: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  exercises: WorkoutExercise[];
  wearableActivities?: LinkedActivity[];
}

// Treenin luonti/päivitys
export interface CreateWorkoutInput {
  name?: string;
  notes?: string | null;
  workoutType?: FilterWorkoutType | null;
  startedAt?: string;
  completedAt?: string | null;
  exercises?: {
    exerciseId: string;
    orderIndex?: number;
    notes?: string | null;
    sets?: {
      setNumber: number;
      reps: number;
      weight?: number | null;
      rpe?: number | null;
      restSeconds?: number | null;
      notes?: string | null;
    }[];
  }[];
}

// API-vastaukset - vastaa backendin todellista rakennetta
export interface WorkoutsListResponse {
  workouts: Workout[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

// Geneerinen sivutettu vastaus (jos tarvitaan muualla)
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface WorkoutStats {
  totalWorkouts: number;
  workoutsLast30Days: number;
  workoutsByType: { type: string; count: number }[];
}

// Lomakkeen tila
export interface WorkoutFormSet {
  tempId: string;
  exerciseId: string;
  exerciseName?: string;
  reps: number;
  weight: number | null;
  rpe: number | null;
}

// Tilastojen tyypit

export interface StatsSummaryResponse {
  thisWeekWorkouts: number;
  lastWeekWorkouts: number;
  weekStreak: number;
  totalWorkouts: number;
  thisMonthWorkouts: number;
  personalRecords: Array<{
    exerciseName: string;
    weight: number;
    date: string;
  }>;
}

export type StatsPeriod = '1m' | '3m' | '6m' | '1y' | 'all';
export type StatsGroupBy = 'week' | 'month';

export interface ExerciseProgressDataPoint {
  date: string;
  maxWeight: number;
  maxReps: number;
  totalSets: number;
}

export interface ExerciseProgressResponse {
  exerciseId: string;
  exerciseName: string;
  data: ExerciseProgressDataPoint[];
  period: string;
}

export interface WorkoutFrequencyDataPoint {
  periodStart: string;
  periodLabel: string;
  workoutCount: number;
}

export interface WorkoutFrequencyResponse {
  data: WorkoutFrequencyDataPoint[];
  groupBy: 'week' | 'month';
  period: string;
  totalWorkouts: number;
  averagePerPeriod: number;
}

export interface UserExerciseForStats {
  id: string;
  name: string;
  muscleGroup: string | null;
  usageCount: number;
}

export interface UserExercisesResponse {
  exercises: UserExerciseForStats[];
}

// Cardio-tilastot
export type CardioActivityType = 'walking' | 'running' | 'snowboarding' | 'all';

export interface CardioProgressDataPoint {
  date: string;
  activityType: string;
  activityName: string | null;
  distanceKm: number | null;
  durationMinutes: number | null;
  paceMinPerKm: number | null;
  avgHeartRate: number | null;
  maxHeartRate: number | null;
  minHeartRate: number | null;
  calories: number | null;
  elevationGain: number | null;
}

export interface CardioProgressSummary {
  totalActivities: number;
  totalDistanceKm: number;
  totalDurationMinutes: number;
  avgPaceMinPerKm: number | null;
  totalElevationGain: number;
  avgHeartRate: number | null;
  lowestMinHeartRate: number | null;
  highestMaxHeartRate: number | null;
}

export interface CardioProgressResponse {
  data: CardioProgressDataPoint[];
  period: string;
  activityType: string;
  summary: CardioProgressSummary;
}

// ============================================
// Wearable & Health Types (Garmin Integration)
// ============================================

// Wearable Provider
export interface WearableProvider {
  id: string;
  name: string;
  displayName: string;
  iconUrl?: string;
}

// Wearable Connection
export interface WearableConnection {
  id: string;
  provider: WearableProvider;
  username: string;
  lastSyncAt: string | null;
  syncEnabled: boolean;
  syncStatus: 'pending' | 'syncing' | 'success' | 'error';
  errorMessage?: string;
  createdAt: string;
}

// Daily Health Summary
export interface DailyHealthSummary {
  date: string;
  hasData: boolean;
  steps: number | null;
  totalCalories: number | null;
  activeCalories: number | null;
  restingHeartRate: number | null;
  avgHeartRate: number | null;
  maxHeartRate: number | null;
  minHeartRate: number | null;
  sleepDurationMinutes: number | null;
  deepSleepMinutes: number | null;
  lightSleepMinutes: number | null;
  remSleepMinutes: number | null;
  awakeDurationMinutes: number | null;
  sleepScore: number | null;
  stressLevel: number | null;
  source: string;
}

// Wearable Activity
export interface WearableActivity {
  id: string;
  externalId: string;
  date: string;
  startTime: string;
  endTime: string | null;
  activityType: string;
  activityName: string | null;
  durationMinutes: number | null;
  calories: number | null;
  avgHeartRate: number | null;
  maxHeartRate: number | null;
  minHeartRate: number | null;
  distance: number | null;
  steps: number | null;
  // Training effect (1.0 - 5.0)
  aerobicTE: number | null;
  anaerobicTE: number | null;
  // Intensity minutes
  moderateMinutes: number | null;
  vigorousMinutes: number | null;
  // Speed (m/s)
  avgSpeed: number | null;
  maxSpeed: number | null;
  // Elevation (meters)
  elevationGain: number | null;
  elevationLoss: number | null;
  linkedWorkout?: {
    id: string;
    name: string | null;
    startedAt: string;
  } | null;
  autoLinked: boolean;
  source: string;
}

// Health Trends
export type HealthPeriod = 'week' | 'month' | '3months';

export interface TrendDataPoint {
  date: string;
  value: number;
}

export interface SleepTrendDataPoint {
  date: string;
  value: number;
  deep: number | null;
  light: number | null;
  rem: number | null;
  awake: number | null;
  score: number | null;
}

export interface WorkoutTrendDataPoint {
  date: string;
  count: number;
}

export interface CalorieTrendDataPoint {
  date: string;
  active: number | null;
  total: number | null;
}

export interface HealthTrendsResponse {
  period: HealthPeriod;
  from: string;
  to: string;
  data: {
    steps: TrendDataPoint[];
    sleep: SleepTrendDataPoint[];
    restingHR: TrendDataPoint[];
    workouts: WorkoutTrendDataPoint[];
    calories: CalorieTrendDataPoint[];
  };
  averages: {
    steps: number;
    sleepMinutes: number;
    sleepScore: number | null;
    restingHR: number;
    workoutsPerWeek: number;
    activeCalories: number;
  };
}

// Sync Response
export interface SyncResponse {
  success: boolean;
  activitiesCount?: number;
  healthDataSynced?: boolean;
  activitiesLinked?: number;
  error?: string;
  message?: string;
}

// Link Suggestion (Workout for linking)
export interface WorkoutLinkSuggestion {
  id: string;
  name: string | null;
  startedAt: string;
  completedAt: string | null;
}

// ============================================
// Training Programs (Treeniohjelmat)
// ============================================

export interface TrainingProgram {
  id: string;
  userId: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  templates?: ProgramTemplate[];
  _count?: {
    templates: number;
  };
}

export interface ProgramTemplate {
  id: string;
  programId: string;
  name: string;
  orderIndex: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  exercises?: ProgramExercise[];
  _count?: {
    workouts: number;
  };
}

export interface ProgramExercise {
  id: string;
  templateId: string;
  exerciseId: string;
  exercise?: Exercise;
  orderIndex: number;
  notes: string | null;
  sets: ProgramExerciseSet[];
}

export interface ProgramExerciseSet {
  id: string;
  programExerciseId: string;
  setNumber: number;
  targetReps: number | null;
  notes: string | null;
}

// Input types for Training Programs
export interface CreateProgramInput {
  name: string;
  description?: string;
}

export interface UpdateProgramInput {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export interface CreateTemplateInput {
  name: string;
  orderIndex?: number;
  notes?: string;
}

export interface UpdateTemplateInput {
  name?: string;
  orderIndex?: number;
  notes?: string;
}

export interface AddProgramExerciseInput {
  exerciseId: string;
  orderIndex?: number;
  notes?: string;
  sets?: {
    setNumber: number;
    targetReps?: number;
    notes?: string;
  }[];
}

export interface StartWorkoutInput {
  name?: string;
  notes?: string;
  startedAt?: string;
}

// Last weights response for exercise
export interface LastWeightsResponse {
  exerciseId: string;
  exerciseName: string;
  lastWorkout: {
    id: string;
    date: string;
    sets: {
      setNumber: number;
      reps: number | null;
      weight: number;
    }[];
  } | null;
}

// Edellisen kerran sarjat (osio 6.2). weight null jos painoa ei kirjattu.
export interface PreviousSetsResponse {
  workoutDate: string;
  sets: {
    weight: number | null;
    reps: number | null;
    rpe: number | null;
  }[];
}

// Liikekohtainen lepoaika-asetus (osio 6.4). null = ei asetettu → globaali oletus.
export interface ExerciseSettingsResponse {
  restSeconds: number | null;
}

// ---- Tilastot, osio 8 (uudet endpointit) ----

export interface MonthMetrics {
  workouts: number;
  totalVolume: number;
  totalSets: number;
  activeDays: number;
}

export interface TopLift {
  exerciseId: string;
  exerciseName: string;
  bestWeight: number;
  workoutId: string;
  isAllTimeBest: boolean;
}

export interface MonthlySummaryResponse extends MonthMetrics {
  prevMonth: MonthMetrics;
  topLifts: TopLift[];
}

export interface VolumeWeek {
  isoWeek: string;
  weekStart: string;
  totalVolume: number;
  totalSets: number;
  workoutCount: number;
}

export interface CalendarDay {
  date: string;
  workouts: { id: string; name: string | null }[];
  totalVolume: number;
}

export interface ExerciseStatsPoint {
  date: string;
  workoutId: string;
  bestWeight: number | null;
  volume: number;
  totalReps: number;
  sets: { weight: number | null; reps: number | null }[];
}

export interface ExerciseStatsResponse {
  exerciseId: string;
  exerciseName: string;
  months: number;
  data: ExerciseStatsPoint[];
}

export type StatsMonths = 3 | 6 | 12;

// ============================================
// Weight Tracking (Painonseuranta)
// ============================================

export interface WeightEntry {
  id: string;
  date: string;
  weight: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WeightEntryInput {
  date: string;
  weight: number;
  notes?: string;
}

export interface WeightUpdateInput {
  weight?: number;
  notes?: string | null;
}

export type WeightPeriod = 'week' | 'month' | '3months' | 'year' | 'all';

export interface WeightStats {
  currentWeight: number;
  startWeight: number;
  minWeight: number;
  maxWeight: number;
  avgWeight: number;
  weightChange: number;
  totalEntries: number;
  bmi: number | null;
  bmiCategory: string | null;
  targetWeight: number | null;
  targetDifference: number | null;
}

export interface WeeklyAverage {
  weekStart: string;
  avgWeight: number;
  entries: number;
}

export interface MonthlyAverage {
  month: string;
  avgWeight: number;
  entries: number;
}

export interface WeightStatsResponse {
  period: WeightPeriod;
  hasData: boolean;
  from: string | null;
  to: string;
  entries: { date: string; weight: number }[];
  stats: WeightStats | null;
  weeklyAverages: WeeklyAverage[];
  monthlyAverages: MonthlyAverage[];
}

// User Profile (Pituus, tavoitepaino)
export interface UserProfile {
  exists: boolean;
  id?: string;
  height: number | null;
  targetWeight: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProfileUpdateInput {
  height?: number | null;
  targetWeight?: number | null;
}

export interface BMIResponse {
  canCalculate: boolean;
  reason?: string;
  message?: string;
  bmi?: number;
  category?: string;
  categoryColor?: string;
  height?: number;
  weight?: number;
  weightDate?: string;
  targetWeight?: number | null;
}


// ============================================
// Kyykkyohjelma (Squat Program)
// ============================================

export type SquatProgramStatus = 'ACTIVE' | 'COMPLETED' | 'ABANDONED';

// Staattinen ohjelman määrittely (ei ID:itä)
export interface SquatProgramSetDef {
  setNumber: number;
  percentage: number;
  reps: number;
  isWorkSet: boolean;
}

export interface SquatProgramWeekDef {
  weekNumber: number;
  description: string;
  sets: SquatProgramSetDef[];
}

// Viikon ennakkokatsaus painoineen (lasketaan backendissä)
export interface SquatWeekWithWeights extends SquatProgramWeekDef {
  sets: (SquatProgramSetDef & { weight: number })[];
}

// Prisma Decimal tulee stringinä API:sta - käsittele Number()-muunnoksella
export interface SquatSet {
  id: string;
  sessionId: string;
  setNumber: number;
  percentage: string | number;
  weight: string | number;
  targetReps: number;
  actualReps: number;
  completed: boolean;
  isWorkSet: boolean;
  completedAt: string | null;
  createdAt: string;
}

export interface SquatSession {
  id: string;
  programId: string;
  weekNumber: number;
  sessionDate: string;
  notes: string | null;
  rpe: number | null;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
  sets: SquatSet[];
  program?: SquatProgram;
}

export interface SquatProgram {
  id: string;
  userId: string;
  name: string;
  oneRepMax: string | number;
  startDate: string;
  endDate: string | null;
  status: SquatProgramStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  sessions?: SquatSession[];
  _count?: { sessions: number };
}

export interface SquatSessionStats {
  sessionId: string;
  weekNumber: number;
  date: string;
  completed: number;
  total: number;
  completionRate: number;
  volume: number;
  rpe: number | null;
  isCompleted: boolean;
}

export interface SquatCompleteResponse {
  session: SquatSession;
  newPR: number | null;
  programCompleted: boolean;
}
